/**
 * ROOT ERROR BOUNDARY — task-xpoqxwdw18.
 *
 * ⛔ WHY THIS EXISTS, STATED HONESTLY: **IT IS NOT A FIX FOR THE REPORTED WHITE PAGE, AND IT IS NOT
 * ESTABLISHED AS ITS CAUSE.** The CEO's console log from the failing page contains NO exception at
 * all, so "something threw" is a hypothesis his own evidence does not support.
 *
 * It ships anyway because of what the app could NOT do for five rounds of investigation: measured at
 * `d5413710a`, admin/src contained **ZERO** error boundaries — no `componentDidCatch`, no
 * `getDerivedStateFromError`, no `react-error-boundary` dependency, and `main.tsx` rendered `<App/>`
 * bare. With no boundary anywhere, a render-phase throw unmounts the ENTIRE root and the user sees a
 * blank document with nothing to report back. **The app had no diagnostic surface, and that is why
 * round after round produced no information.**
 *
 * ⇒ This does not make the app correct. It makes the app ABLE TO TELL US WHAT WENT WRONG.
 *
 * ⛔ NO SILENT FALLBACKS (CLAUDE.md). This boundary must never be the thing that hides the next bug:
 *   · the raw `error` and `errorInfo` objects go to `console.error` UNMODIFIED, so devtools keeps the
 *     real stack and its source-map links;
 *   · the message, stack and component stack are rendered ON SCREEN and are selectable, so a
 *     non-technical reporter can paste them without opening devtools at all;
 *   · nothing is summarised, truncated or replaced with a friendly string.
 *
 * ⛔⛔ THE RULE THIS COMPONENT EXISTS TO ILLUSTRATE, LEARNED BY BREAKING IT THREE TIMES IN ONE PR:
 * A DIAGNOSTIC SURFACE MAY ONLY STATE WHAT IT OBSERVED. What completed, what is safe, what to do
 * next — all of those are claims about a system it cannot see from inside a catch block.
 *     round 1   the boundary fell through on a falsy throw ...... a mechanism that assumed the good case
 *     round 2   "nothing was saved and nothing was sent" ........ a claim it could not measure
 *     round 3   "reloading the page is safe" .................... the same claim, written INTO THE FIX
 * ⇒ Round 3 was introduced BY the correction for round 2, which is what makes it a reflex rather than
 *   an oversight: the confident version is the one that comes out first.
 * ⚠️ AND THE COST IS ASYMMETRIC. An under-confident error screen wastes a minute of someone's time; an
 * over-confident one causes a duplicate write. There is no symmetric penalty for being careful here,
 * so when in doubt, say less.
 *
 * ⛔⛔ AND IT MUST NOT REASSURE. The first version said "Nothing was saved and nothing was sent." A
 * RENDER error says the UI failed to draw — it says NOTHING about what already went over the wire. A
 * mutation can return 200, update state, and the NEXT render can throw, at which point that sentence
 * tells an operator with full confidence that nothing happened. They retry, and on this admin that
 * means a duplicate enrichment, regeneration or save.
 * 🔑 THIS IS THE SILENT-FALLBACK RULE POINTING THE OTHER WAY: not swallowing a failure, but ASSERTING
 * A SUCCESS CONDITION NOBODY MEASURED. Same defect, opposite sign — and the reassuring one is worse,
 * because a blank screen prompts a check while a confident "nothing was saved" gets believed.
 * ⇒ The copy now says what is true: the page failed to render, and whether the last action completed
 *   is UNKNOWN.
 *
 * ⚠️ STYLED WITH INLINE STYLES ON PURPOSE — NOT Tailwind. One of the live hypotheses for a blank page
 * is that the stylesheet is missing or mis-applied. A fallback UI that depends on the app's CSS can
 * render invisibly in exactly the situation it exists to report, which would look identical to the
 * blank page it is meant to explain. Inline styles cannot fail that way.
 */
import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  /**
   * ⛔ AN EXPLICIT FLAG, NOT `error !== null`. JavaScript permits `throw null`, `throw undefined`,
   * `throw false`, `throw 0` and `throw ''`, and React hands whatever was thrown straight through.
   * Keying the fallback on the truthiness of the caught value means a falsy throw renders the
   * FAILING CHILD AGAIN — recreating the blank screen this component exists to prevent, in the one
   * component that is supposed to make blank screens impossible. Found in review of #5386.
   */
  caught: boolean;
  /** `unknown`, because it genuinely is. Narrowed at format time, never assumed to be an Error. */
  error: unknown;
  componentStack: string | null;
}

/**
 * Describe ANY thrown value without discarding it. A non-Error throw is rarer but not exotic, and
 * "undefined: undefined" on screen is worse than useless — it looks like the boundary itself broke.
 */
function describeThrown(value: unknown): { headline: string; detail: string } {
  try {
    return describeThrownUnsafe(value);
  } catch {
    // ⛔⛔ THE FINAL FALLBACK IS A CONSTANT, AND IT HAS TO BE. Everything above can throw: a revoked
    // Proxy, an object whose `toJSON` AND `toString` both throw, an Error subclass with a throwing
    // `.stack` accessor. This is the ROOT boundary — if formatting escapes `render()` there is
    // nowhere else to catch it and the page goes blank, which is the exact outcome this component
    // exists to prevent, produced BY the component, for the third time in this PR's life.
    // ⚠️ A guard containing a call is not a guard. No interpolation, no coercion, no property access.
    return {
      headline: 'A value was thrown that could not be described.',
      detail: 'Formatting the thrown value itself threw. The failure is real; its details are unreadable. See the browser console for the raw object.',
    };
  }
}

function describeThrownUnsafe(value: unknown): { headline: string; detail: string } {
  if (value instanceof Error) {
    // ⚠️ `name`, `message` and `stack` are ACCESSORS on a subclass and can throw. Reading them here
    // is deliberate — the outer guard is what makes it safe, not optimism about these three.
    return {
      headline: `${value.name}: ${value.message}`,
      detail: value.stack || '(no stack on the error object)',
    };
  }
  // Not an Error. Say so plainly and show what it actually was, including for falsy values —
  // `typeof null` is "object", so the null case is named separately or it reads as an empty object.
  const kind = value === null ? 'null' : typeof value;
  let rendered: string;
  try {
    rendered = typeof value === 'string' ? value : JSON.stringify(value) ?? String(value);
  } catch {
    // JSON.stringify throws on circular structures and BigInt. Try coercion, which is a SEPARATE
    // failure mode — `String(value)` throws on a revoked Proxy or a throwing `Symbol.toPrimitive`.
    // ⛔ NOT A SILENT FALLBACK: each step says which one failed, so the reader knows the value existed.
    try {
      rendered = `(value could not be serialised: ${String(value)})`;
    } catch {
      rendered = '(value could neither be serialised nor converted to a string)';
    }
  }
  return {
    headline: `Non-Error value thrown (typeof ${kind})`,
    detail: `thrown value: ${rendered}\n(no stack — only Error objects carry one)`,
  };
}

/**
 * ⚠️ ORIGIN + PATHNAME ONLY — the query string and fragment are NOT shown. This block is explicitly
 * offered to a user to paste into a bug report, and admin URLs can carry tokens, OAuth parameters
 * and email addresses. Their PRESENCE is reported so nobody is misled into thinking the URL was
 * bare, but their contents are not. Raised in review of #5386.
 */
function safeLocation(): string {
  if (typeof window === 'undefined' || !window.location) return '(no window)';
  const { origin, pathname, search, hash } = window.location;
  const withheld = [search ? 'query' : null, hash ? 'fragment' : null].filter(Boolean);
  return `${origin}${pathname}` + (withheld.length ? `   [${withheld.join(' + ')} withheld — may contain tokens]` : '');
}

const box: React.CSSProperties = {
  padding: '24px',
  margin: '0',
  minHeight: '100vh',
  background: '#fff',
  color: '#111',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '13px',
  lineHeight: 1.5,
};

const pre: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#f6f6f6',
  border: '1px solid #ddd',
  borderRadius: '4px',
  padding: '12px',
  margin: '8px 0 16px',
  userSelect: 'text',
};

export class RootErrorBoundary extends React.Component<Props, State> {
  state: State = { caught: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { caught: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // ⛔ RAW OBJECTS, NOT STRINGS. Passing `error` itself keeps the stack live and source-mapped in
    // devtools; `String(error)` would flatten it to a message and lose the thing worth having.
    console.error('[RootErrorBoundary] UNCAUGHT RENDER ERROR — the app tree was unmounted:', error);
    console.error('[RootErrorBoundary] component stack:', errorInfo?.componentStack);
    this.setState({ componentStack: errorInfo?.componentStack ?? null });
  }

  render() {
    const { caught, error, componentStack } = this.state;
    if (!caught) return this.props.children;
    const { headline, detail } = describeThrown(error);

    // Assembled as ONE selectable block so a reporter can drag-select it in a single motion. A copy
    // button is deliberately absent: navigator.clipboard is unavailable in some contexts and a button
    // that silently does nothing is worse than no button.
    const report = [
      `PageMind admin — render error`,
      `when:  ${new Date().toISOString()}`,
      `where: ${safeLocation()}`,
      ``,
      headline,
      ``,
      detail,
      ``,
      `component stack:`,
      componentStack || '(no component stack — componentDidCatch has not run yet)',
    ].join('\n');

    return (
      <div style={box} data-testid="root-error-boundary">
        <h1 style={{ fontSize: '18px', margin: '0 0 8px' }}>This page failed to render.</h1>
        <p style={{ margin: '0 0 16px', maxWidth: '70ch' }}>
          <strong>Whether your last action completed is unknown — check before repeating it.</strong> A
          render failure means the screen could not be drawn; it says nothing about what already reached
          the server, so a save or a regeneration may well have gone through.{' '}
          <strong>Please copy everything in the box below</strong> and paste it into the bug report — it is
          the whole of what went wrong, and without it the failure looks like a blank screen from the outside.
        </p>
        <pre style={pre} data-testid="root-error-boundary-report">{report}</pre>
        <p style={{ margin: 0, color: '#555' }}>
          Reloading may recover the page, but verify your last action first — a reload discards unsaved
          changes and re-runs the page's start-up requests. If it happens again, the text above will say
          whether it is the same fault.
        </p>
      </div>
    );
  }
}

export default RootErrorBoundary;
